import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Header from "../../components/header";
import { getMyAds, submitAd } from "../../functions/ads";
import "./style.css";

const emptyForm = {
  title: "",
  description: "",
  ctaText: "Learn more",
  destinationUrl: "",
  startDate: "",
  endDate: "",
};

function statusLabel(ad) {
  if (ad.moderationStatus === "pending") return "Pending review";
  if (ad.moderationStatus === "rejected") return "Rejected";
  if (ad.endDate && new Date(ad.endDate) < new Date()) return "Expired";
  return ad.isActive ? "Active" : "Inactive";
}

function statusClass(ad) {
  if (ad.moderationStatus === "pending") return "pending";
  if (ad.moderationStatus === "rejected") return "rejected";
  if (ad.endDate && new Date(ad.endDate) < new Date()) return "rejected";
  return ad.isActive ? "active" : "inactive";
}

export default function MyAds() {
  const { user } = useSelector((state) => ({ ...state }));
  const [ads, setAds] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [mediaFile, setMediaFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const loadAds = async () => {
    if (!user?.token) return;
    setLoading(true);
    try {
      setAds(await getMyAds(user.token));
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load your ads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.token) return undefined;
    let cancelled = false;
    setLoading(true);
    getMyAds(user.token)
      .then((data) => {
        if (!cancelled) setAds(data);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.message || "Failed to load your ads");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.token]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.title.trim()) {
      setError("Title is required");
      return;
    }
    if (!form.destinationUrl.trim()) {
      setError("Destination URL is required");
      return;
    }
    if (!mediaFile) {
      setError("Please upload an image or video");
      return;
    }

    const payload = new FormData();
    payload.append("title", form.title.trim());
    payload.append("description", form.description.trim());
    payload.append("ctaText", form.ctaText.trim() || "Learn more");
    payload.append("destinationUrl", form.destinationUrl.trim());
    payload.append("startDate", form.startDate);
    payload.append("endDate", form.endDate);
    payload.append("media", mediaFile);

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await submitAd(payload, user.token);
      setForm(emptyForm);
      setMediaFile(null);
      setSuccess("Your advertisement was submitted and is pending admin approval.");
      await loadAds();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to submit advertisement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="my_ads_page">
      <Header page="my-ads" />
      <div className="my_ads_container">
        <div className="my_ads_card">
          <div className="my_ads_header">
            <h1>Advertise on UsmaApp</h1>
            <p>
              Submit an ad for the feed. It stays hidden until an admin approves it.
              You will be notified when it is approved or rejected.
            </p>
          </div>

          <form className="my_ads_form" onSubmit={handleSubmit}>
            {error ? <div className="my_ads_error">{error}</div> : null}
            {success ? <div className="my_ads_success">{success}</div> : null}

            <label>
              Title
              <input name="title" value={form.title} onChange={handleChange} maxLength={120} required />
            </label>
            <label>
              Destination URL
              <input
                name="destinationUrl"
                value={form.destinationUrl}
                onChange={handleChange}
                placeholder="https://example.com"
                required
              />
            </label>
            <label>
              Description
              <textarea name="description" value={form.description} onChange={handleChange} rows={4} maxLength={1000} />
            </label>
            <label>
              CTA button text
              <input name="ctaText" value={form.ctaText} onChange={handleChange} maxLength={40} />
            </label>
            <div className="my_ads_dates">
              <label>
                Start date
                <input type="date" name="startDate" value={form.startDate} onChange={handleChange} />
              </label>
              <label>
                End date
                <input type="date" name="endDate" value={form.endDate} onChange={handleChange} />
              </label>
            </div>
            <label className="my_ads_upload">
              Image or video
              <span className="my_ads_upload_box">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp,image/avif,video/mp4,video/webm,video/ogg"
                  onChange={(event) => setMediaFile(event.target.files?.[0] || null)}
                />
                <span>{mediaFile ? mediaFile.name : "Choose an image or video"}</span>
              </span>
            </label>
            <button type="submit" disabled={saving}>
              {saving ? "Submitting..." : "Submit for approval"}
            </button>
          </form>
        </div>

        <div className="my_ads_card my_ads_list_card">
          <h2>Your advertisements</h2>
          {loading ? <p>Loading...</p> : null}
          {!loading && ads.length === 0 ? <p>You have not submitted any ads yet.</p> : null}
          <ul className="my_ads_list">
            {ads.map((ad) => (
              <li key={ad._id}>
                <div>
                  <strong>{ad.title}</strong>
                  <span className={`my_ads_status is-${statusClass(ad)}`}>{statusLabel(ad)}</span>
                </div>
                {ad.moderationStatus === "rejected" && ad.rejectionReason ? (
                  <p>Reason: {ad.rejectionReason}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
